import { useEffect, useRef, useState } from 'react';
import Agendamentos from '../../services/Agendamentos';
import { formatDateBR, isValidDate, shortWeekDay, todayText } from '../../utilities/agendamentoSeries';

// Duração do horário proposto: a mesma do modal quando o Início muda sem duração (e a mais comum na agenda)
export const NEXT_SLOT_DURATION = 15;

const EMPTY_TIME = '00:00';
const IDLE = { state: 'idle', note: null, suggestion: null };

const REASON_TEXT = {
  NO_SERVICE_HOURS: 'O doutor não tem dias de atendimento cadastrados: informe o horário.',
  NO_FREE_SLOT: 'O doutor não tem horário livre nos próximos 60 dias: informe o horário.',
};

const dayText = (date) => `${shortWeekDay(date)} ${formatDateBR(date)}`;

// Agendamento novo: preenche Data, Início e Fim com o primeiro horário livre do doutor (GET /agendamentos/next-slot, que lê
// os dias de atendimento e a agenda), nunca antes de agora. Enquanto a data for a da tela, a busca começa nela e pode
// levar a data ao próximo dia livre. Depois que a pessoa escolhe a data, ela não muda sozinha: só o horário daquele dia
// (ou o próximo dia livre, para usar com um clique). Só enquanto o Início e o Fim forem os do modal (00:00) ou os
// preenchidos aqui: depois que a pessoa mexe neles, nada muda sozinho
export function useNextSlot({ enabled, tecnicoId, date, startTime, endTime, setValue }) {
  const [result, setResult] = useState(IDLE);
  const sequence = useRef(0);
  const filled = useRef(null); // { date, start_time, end_time } escritos aqui por último
  const settled = useRef(null); // { tecnicoId, date } da última busca concluída (ou dispensada)
  const screenDate = useRef(null); // a data com que o modal abriu (a da tela)
  const knownDate = useRef(null); // a última data vista ou escrita aqui: outra no campo foi a pessoa que escolheu
  const chosen = useRef(false); // a pessoa escolheu a data

  const automatic = (startTime === EMPTY_TIME && endTime === EMPTY_TIME)
    || (Boolean(filled.current) && startTime === filled.current.start_time && endTime === filled.current.end_time);

  function fill(slot) {
    filled.current = slot;
    knownDate.current = slot.date;
    setValue('data_inicial', slot.date);
    setValue('hora_inicial', slot.start_time);
    setValue('hora_final', slot.end_time);
  }

  // O horário preenchido era de outro doutor ou de outro dia: volta para o 00:00 do modal
  function clearFilled() {
    if (!filled.current) return;
    filled.current = null;
    setValue('hora_inicial', EMPTY_TIME);
    setValue('hora_final', EMPTY_TIME);
  }

  useEffect(() => {
    if (!enabled) {
      // Fechou, salvou ou virou "próximas sessões": a próxima abertura começa do zero
      sequence.current++;
      filled.current = null;
      settled.current = null;
      screenDate.current = null;
      knownDate.current = null;
      chosen.current = false;
      setResult(IDLE);
      return undefined;
    }

    let typedDate = false;
    if (knownDate.current === null) {
      screenDate.current = date;
      knownDate.current = date;
    } else if (date !== knownDate.current) {
      knownDate.current = date;
      chosen.current = true;
      typedDate = true;
    }

    if (!automatic || !tecnicoId) {
      sequence.current++;
      setResult(IDLE);
      return undefined;
    }

    if (settled.current?.tecnicoId === tecnicoId && settled.current?.date === date) return undefined;

    const keepDate = chosen.current;
    const from = keepDate ? date : screenDate.current;
    const validFrom = isValidDate(from);
    const today = todayText();

    // Dia que já passou (registro de um atendimento antigo) ou data incompleta, escolhidos pela pessoa: nada a propor
    if (keepDate && (!validFrom || from < today)) {
      settled.current = { tecnicoId, date };
      setResult(IDLE);
      return undefined;
    }

    const mine = ++sequence.current;
    setResult({ state: 'loading', note: null, suggestion: null });

    // A data digitada muda a cada parte (dia, mês, ano): espera a pessoa terminar. O doutor escolhido busca na hora
    const timer = setTimeout(() => {
      Agendamentos.nextSlot({ tecnico_id: tecnicoId, start_date: validFrom && from > today ? from : today, duration_minutes: NEXT_SLOT_DURATION })
        .then(({ data }) => {
          if (mine !== sequence.current) return;
          const slot = data?.slot ?? null;

          if (slot && (!keepDate || slot.date === date)) {
            settled.current = { tecnicoId, date: slot.date };
            fill(slot);

            let note = null;
            if (slot.date !== from)
              note = validFrom && from >= today
                ? `Sem horário livre do doutor em ${dayText(from)}: a data foi para o próximo dia com horário livre.`
                : 'A data foi para o primeiro horário livre do doutor, a partir de hoje.';
            setResult({ state: 'ready', note, suggestion: null });
            return;
          }

          settled.current = { tecnicoId, date };
          clearFilled();
          setResult({
            state: 'ready',
            note: slot
              ? `Sem horário livre do doutor neste dia. O próximo é ${dayText(slot.date)} às ${slot.start_time}.`
              : (REASON_TEXT[data?.reason] ?? REASON_TEXT.NO_FREE_SLOT),
            suggestion: slot,
          });
        })
        .catch(() => {
          if (mine !== sequence.current) return;
          settled.current = { tecnicoId, date };
          setResult({ state: 'failed', note: 'Não foi possível buscar o primeiro horário livre do doutor: informe o horário.', suggestion: null });
        });
    }, typedDate ? 400 : 0);

    return () => clearTimeout(timer);
  }, [enabled, tecnicoId, date, automatic]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Usar este horário": o próximo dia livre, proposto quando o dia escolhido não tem horário
  function applySuggestion() {
    if (!result.suggestion) return;
    settled.current = { tecnicoId, date: result.suggestion.date };
    fill(result.suggestion);
    setResult(IDLE);
  }

  return { ...result, applySuggestion };
}
